import React, { useEffect, useMemo, useRef, useState } from 'react';
import { type OrderDetails } from '../../../services/orders.service';
import { Row, Label, Section, Icon, TextInput, SelectInput } from './orderform';
import DateTimePicker from '../../../components/DateTimePicker';
import {
  parseMdYHm,
  formatMdYHm,
  parseMdYDate,
  formatMdYDate,
} from '../../../utils/dateUtils';
import {
  isTemplatePronumber,
  isTemplateApproved,
  templateFieldEditable,
} from '../templateFieldPolicy';
import { useCanEditOrderLcFields } from '../useCanEditOrderLcFields';
import { useOrderFieldDisplayContext } from '../OrderFieldDisplayContext';
import { getTraceCarriers, type TraceCarrier } from '../../../services/traceCarriers.service';
import { type TraceCarrierType } from '../../../services/tracking.service';
import { isThdBillto } from '../../../constants/billtosCustomers';
import BulkFieldCheckbox from '../changeAll/BulkFieldCheckbox';
import SingleSelect from '../../../components/SingleSelect';

// Order `mode` values (legacy defs.pl buildmodes_data). Shared by the Tracing
// Mode select for both templates and orders.
const MODE_OPTIONS = ['', 'AWR', 'DIV', 'MLB', 'TRUCK'].map((v) => ({
  value: v,
  label: v === '' ? '—' : v,
}));

// Hardcoded V1 dropdown lists (defs.pl). Leading '' = the unselected row.
const CUSTOMS_STATUS_OPTIONS = ['', 'HOLD', 'RELEASED', 'REL/TRAN'];
const RAILBILL_STATUS_OPTIONS = [
  '',
  'PENDING RAIL BILLING',
  'RAIL BILLING SENT',
  'RAIL BILLED',
  'CANCEL RAIL BILLING - PENDING',
  'CANCEL RAIL BILLING - APPROVED',
];
const CONTACT_TYPE_OPTIONS = ['', 'EMAIL', 'WEBSITE'];

/**
 * Display of the V1 "Tracing" tab. The layout mirrors the V1
 * `@edit_layout_tracing` array (cms-master/lib/defs.pl:2601-2606) split
 * into three columns:
 *   1. Trace Run Status (container, last trace event)
 *   2. Vessel / Rail / Customs / Holds
 *   3. Gnosis fields
 *
 * When the `editing` prop is provided, the tracing run status fields and
 * key vessel/import fields render as inline inputs and save via the same
 * `updateOrderField` mechanism used by OrderFormView.
 */

/**
 * Subset of the `useOrderEditing` hook surface that TracingView actually uses.
 * Matches the pattern used by OrderFormView and AddlInfoView exactly.
 */
export interface TracingEditing {
  editedOrderFields: Record<string, any>;
  updateOrderField: (name: string, value: any) => void;
}

export interface TracingViewProps {
  details: Partial<OrderDetails> | null;
  editing?: TracingEditing;
  /** CSR/LC template-edit gate. On a template, Mode only opens when this is
   *  true. Defaults to editable; ignored for a normal order. */
  canEditTemplate?: boolean;
  /** Optional ➡️ run-trace control for the Trace Container row. A render-prop
   *  (not a plain node) because the button forces the carrier side derived from
   *  the locally-selected Trace Container: rail if it's the order's terminal,
   *  scac if it's the SSLine, else undefined (backend auto-detects). Since v2
   *  routes every trace through the single auto-routing call, this ➡️ is the
   *  only control on the row — it subsumes v1's separate gwiTrace-info (ⓘ).
   *  Kept as a prop so this component stays presentational (no network/toast
   *  in tests). */
  traceRun?: (tracetype?: TraceCarrierType, carrier?: string) => React.ReactNode;
  /** Fresh order values from the latest trace, overlaid on display *under* the
   *  user's edits (edited > traced > fetched) so a trace fills fields in place
   *  without a re-fetch. Keyed by OrderDetails key. */
  tracedOverlay?: Record<string, unknown>;
  /** OrderDetails keys the latest trace changed — highlighted on-screen. */
  tracedKeys?: string[];
}

const PLACEHOLDER = '—';
const LABEL_W_LEFT = 116;
const LABEL_W_MID = 144;
const LABEL_W_RIGHT = 132;

interface ReadValueProps {
  value?: unknown;
  mono?: boolean;
  width?: number;
  ariaLabel?: string;
  /** Yellow "updated by this trace" highlight (mirrors the trace modal). */
  highlight?: boolean;
}
const ReadValue: React.FC<ReadValueProps> = ({ value, mono, width, ariaLabel, highlight }) => {
  const text = value == null || value === '' ? PLACEHOLDER : String(value);
  const isEmpty = text === PLACEHOLDER;
  const cls = [
    'inline-flex items-center h-6 px-1.5 text-[0.75rem] truncate',
    '',
    highlight
      ? 'rounded bg-yellow-200 font-medium text-slate-900 dark:bg-yellow-500/30 dark:text-yellow-100'
      : isEmpty
        ? 'text-slate-400 dark:text-gray-500'
        : 'text-slate-900 dark:text-gray-100',
    width ? '' : 'flex-1 min-w-0',
  ]
    .filter(Boolean)
    .join(' ');
  const style = width ? { width: `${width}px` } : undefined;
  return (
    <span
      className={cls}
      style={style}
      aria-label={ariaLabel}
      title={highlight ? 'Updated by this trace' : isEmpty ? undefined : text}
    >
      {text}
    </span>
  );
};

const Divider: React.FC = () => (
  <div className="my-1.5 border-t border-dashed border-slate-200 dark:border-gray-700" />
);

const s = (v: unknown): string => (v == null ? '' : String(v));

const TracingView: React.FC<TracingViewProps> = ({
  details,
  editing,
  canEditTemplate = true,
  traceRun,
  tracedOverlay,
  tracedKeys,
}) => {
  // Overlay the latest trace's fresh values UNDER the user's edits (edited >
  // traced > fetched) so a trace fills the on-screen fields in place. Merging
  // into `d` here means every field render (d.xxx / fv) picks it up with no
  // per-field wiring; `isHl` drives the yellow "updated by this trace" mark.
  const d = { ...(details ?? {}), ...(tracedOverlay ?? {}) } as Partial<OrderDetails>;
  const hlKeys = new Set(tracedKeys ?? []);
  const isHl = (key: string): boolean => hlKeys.has(key);
  // EXPORT gets the slimmer export layout. RAIL TO PORT does NOT — V1 has one
  // tracing layout for every move type (defs.pl @edit_layout_tracing) and RTP
  // hides none of its column-2 fields, so RTP renders the full layout and the
  // display config decides visibility. (Order Info still groups RTP with EXPORT.)
  const isExport = d.moveType === 'EXPORT';
  // The Home-Depot (THD) LFD row only applies to orders billed to THD — same
  // billto check the trouble-ticket / reschedule THD gates use.
  const isThd = isThdBillto(d.billTo);

  const ef = editing?.editedOrderFields ?? {};
  const upd = editing?.updateOrderField;

  // On a template every Tracing field is State 1 (locked) EXCEPT Mode, which is
  // State 3 (editable regardless of approval). Orders keep the `editing`-driven
  // behavior for every field.
  const isTemplate = isTemplatePronumber(d.pronumber);
  const isApproved = isTemplate && isTemplateApproved(d);
  const tracingEditable = isTemplate ? false : !!editing;
  // Mode (o.mode) is CSR/LC-only on orders — gate the ORDER branch behind the
  // order-lc-fields permission (its cog lives on the Order Info / Addl Info LC
  // field groups; this field shares the same component). Templates use the
  // template-edit gate.
  const canEditOrderLcFields = useCanEditOrderLcFields();
  const modeEditable = isTemplate
    ? canEditTemplate && !!editing && templateFieldEditable('traceMode', { isApproved })
    : !!editing && canEditOrderLcFields;
  // Everything newly opened on this tab (vessel/voyage, sail/arrival dates,
  // grounded, booking/equip, customs, pre-advise, rail-bill status, scheduling
  // / rail-pickup contacts) is CSR/LC-only, same order-lc-fields gate as Mode.
  // availableGoodToGoDatetime stays LC-only too. Templates lock them all
  // (tracingEditable is already false on templates). NOTE: the Trace Container
  // is NOT gated here — it's a tracing helper (see below), always interactive.
  const lcEditable = tracingEditable && canEditOrderLcFields;

  /** Effective value: edited wins, then the traced overlay, then details (the
   *  last two are already merged into `d`). */
  const fv = (key: string): unknown =>
    ef[key] !== undefined ? ef[key] : (d as any)[key];

  /** Read-only field with trace overlay + highlight, keyed by OrderDetails key.
   *  Use in place of a bare <ReadValue> for trace-updatable fields so the field
   *  shows the traced value and lights up yellow when the trace changed it. */
  const rv = (key: string, ariaLabel: string, opts: { mono?: boolean; width?: number } = {}) => (
    <ReadValue
      value={fv(key)}
      mono={opts.mono}
      width={opts.width}
      ariaLabel={ariaLabel}
      highlight={isHl(key)}
    />
  );

  // "Trace Container" is a tracing-only helper — pick a traceable carrier/rail
  // (dotrace SCACs + rails) to run a trace against, then hit the ➡️. It is NOT
  // an order field: it defaults blank, lives in local state, and is never
  // saved. Always interactive (not gated on edit mode) so a trace can be run
  // straight from a read-only view, matching v1. The list is fetched once on
  // mount (module-cached in the service).
  const [carrierOptions, setCarrierOptions] = useState<TraceCarrier[]>([]);
  const [traceUnit, setTraceUnit] = useState<string>('');
  useEffect(() => {
    let cancelled = false;
    getTraceCarriers()
      .then((opts) => {
        if (!cancelled) setCarrierOptions(opts);
      })
      .catch(() => {
        /* leave options empty; the field just shows the blank row */
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const containerSelectOptions = useMemo(() => {
    const opts = carrierOptions.map((c) => ({ value: c.carrier, label: c.carrier }));
    // Make sure the current selection (e.g. the seeded last-traced carrier) is
    // always shown, even if it isn't in the dotrace list.
    if (traceUnit && !carrierOptions.some((c) => c.carrier === traceUnit)) {
      opts.unshift({ value: traceUnit, label: traceUnit });
    }
    return opts;
  }, [carrierOptions, traceUnit]);
  // Carrier code → legacy trace side, so the ➡️ forces the right tracer even
  // when the picked unit differs from the order's saved rail/scac.
  const typeByCarrier = useMemo(
    () => new Map(carrierOptions.map((c) => [c.carrier, c.type])),
    [carrierOptions],
  );
  // Default the Trace Container to the order's last-traced carrier
  // (traceRunCarrier) on load and whenever the order changes, until the user
  // picks something else. seededProRef tracks which pronumber we've defaulted so
  // a manual pick isn't clobbered and navigating to another PRO re-seeds.
  const lastTraceCarrier = s((d as any).traceRunCarrier);
  const seededProRef = useRef<unknown>(null);
  useEffect(() => {
    if (seededProRef.current === d.pronumber) return;
    if (!lastTraceCarrier) return; // wait until the last carrier is known
    seededProRef.current = d.pronumber;
    setTraceUnit(lastTraceCarrier);
  }, [d.pronumber, lastTraceCarrier]);
  // Map the selected Trace Container unit to the legacy carrier side so the ➡️
  // traces the right provider. Prefer the carrier list's own type (works for ANY
  // pick, not just the order's own rail/scac); fall back to matching the order's
  // saved rail/scac (covers a seeded last-traced carrier absent from the list);
  // else undefined (let the backend auto-detect).
  const railVal = s((d as any).rail);
  const scacVal = s((d as any).scac);
  const selectedTraceType: TraceCarrierType | undefined =
    (traceUnit && typeByCarrier.get(traceUnit)) ||
    (traceUnit && traceUnit === railVal
      ? 'rail'
      : traceUnit && traceUnit === scacVal
        ? 'scac'
        : undefined);

  // Config-driven movetype visibility (order_field_display_rules). `hidden(key)`
  // is the single source of truth for whether a field shows on this movetype;
  // the isExport branch stays as LAYOUT (labels/widths/groupings), each field
  // render is guarded by the config. Safe default (no provider) = nothing hidden.
  const disp = useOrderFieldDisplayContext();
  const hidden = (key: string): boolean => disp.isFieldHidden(key);

  // --- Inline-edit render helpers -------------------------------------------
  // Each returns an editable input (when the field's gate is open) or a
  // ReadValue. `width` fixes the cell; omitting it lets the cell flex to fill
  // the row — so the "first of a pair" passes a width and the "second" flexes,
  // matching the read-only proportions. Datetime pairs pass no width on both so
  // they split evenly and keep the time visible.
  const editText = (
    key: string,
    ariaLabel: string,
    opts: { width?: number; mono?: boolean } = {},
  ) =>
    lcEditable ? (
      <TextInput
        value={s(fv(key))}
        onChange={(v) => upd?.(key, v)}
        width={opts.width}
        mono={opts.mono}
        ariaLabel={ariaLabel}
        placeholder={ariaLabel}
        highlight={isHl(key) ? 'yellow' : undefined}
      />
    ) : (
      <ReadValue value={fv(key)} mono={opts.mono} width={opts.width} ariaLabel={ariaLabel} highlight={isHl(key)} />
    );

  const editSelect = (
    key: string,
    ariaLabel: string,
    options: string[],
    opts: { width?: number } = {},
  ) =>
    lcEditable ? (
      <SelectInput
        value={s(fv(key))}
        options={options}
        width={opts.width}
        onChange={(v) => upd?.(key, v)}
        ariaLabel={ariaLabel}
        highlight={isHl(key) ? 'yellow' : undefined}
      />
    ) : (
      <ReadValue value={fv(key)} width={opts.width} ariaLabel={ariaLabel} highlight={isHl(key)} />
    );

  const editDate = (key: string, ariaLabel: string, opts: { width?: number } = {}) => {
    if (!lcEditable)
      return <ReadValue value={fv(key)} mono width={opts.width} ariaLabel={ariaLabel} highlight={isHl(key)} />;
    const picker = (
      <DateTimePicker
        mode="date"
        inputVariant="compact"
        selectionType="single"
        autoApply
        usePortal
        openDirection="down"
        autoFlip
        labelType="none"
        placeholder={ariaLabel}
        ariaLabel={ariaLabel}
        className="w-full [&_div]:!min-w-0"
        value={parseMdYDate(s(fv(key)))}
        onChange={(v) => upd?.(key, formatMdYDate(v as Date | null))}
      />
    );
    const ring = isHl(key) ? 'rounded ring-2 ring-yellow-400 dark:ring-yellow-500/50' : '';
    return opts.width ? (
      <div className={ring || undefined} style={{ width: opts.width }}>{picker}</div>
    ) : (
      <div className={`flex-1 min-w-0 ${ring}`.trim()}>{picker}</div>
    );
  };

  const editDateTime = (key: string, ariaLabel: string, opts: { width?: number } = {}) => {
    if (!lcEditable)
      return <ReadValue value={fv(key)} mono width={opts.width} ariaLabel={ariaLabel} highlight={isHl(key)} />;
    const picker = (
      <DateTimePicker
        mode="datetime"
        inputVariant="compact"
        selectionType="single"
        use24Hour
        usePortal
        openDirection="down"
        autoFlip
        labelType="none"
        placeholder={ariaLabel}
        ariaLabel={ariaLabel}
        className="w-full [&_div]:!min-w-0"
        value={parseMdYHm(s(fv(key)))}
        onChange={(v) => upd?.(key, formatMdYHm(v as Date | null))}
      />
    );
    const ring = isHl(key) ? 'rounded ring-2 ring-yellow-400 dark:ring-yellow-500/50' : '';
    return opts.width ? (
      <div className={ring || undefined} style={{ width: opts.width }}>{picker}</div>
    ) : (
      <div className={`flex-1 min-w-0 ${ring}`.trim()}>{picker}</div>
    );
  };

  // Inline datetime field with an explicit edit gate — some tracing pickers use
  // `tracingEditable` (not the LC gate). Same overlay + highlight as
  // editDateTime: edit mode shows the picker (fv value, yellow ring when traced),
  // read mode a highlighted ReadValue.
  const editDateTimeGated = (
    key: string,
    ariaLabel: string,
    gate: boolean,
    placeholder: string = ariaLabel,
  ) =>
    gate ? (
      <div
        className={`flex-1 min-w-0 ${
          isHl(key) ? 'rounded ring-2 ring-yellow-400 dark:ring-yellow-500/50' : ''
        }`.trim()}
      >
        <DateTimePicker
          mode="datetime"
          inputVariant="compact"
          selectionType="single"
          use24Hour
          usePortal
          openDirection="down"
          autoFlip
          labelType="none"
          placeholder={placeholder}
          ariaLabel={ariaLabel}
          className="w-full [&_div]:!min-w-0"
          value={parseMdYHm(s(fv(key)))}
          onChange={(v) => upd?.(key, formatMdYHm(v as Date | null))}
        />
      </div>
    ) : (
      rv(key, ariaLabel, { mono: true })
    );

  const editTextArea = (key: string, ariaLabel: string) =>
    lcEditable ? (
      <textarea
        value={s(fv(key))}
        onChange={(e) => upd?.(key, e.target.value)}
        aria-label={ariaLabel}
        rows={2}
        placeholder={ariaLabel}
        className="flex-1 min-w-0 text-[0.75rem] leading-snug rounded border border-slate-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-1.5 py-1 resize-y text-slate-900 dark:text-gray-100"
      />
    ) : (
      <div className="flex-1 min-w-0 text-[0.75rem] text-slate-900 dark:text-gray-100 whitespace-pre-wrap break-words leading-snug px-1.5">
        {(d as any)[key] || PLACEHOLDER}
      </div>
    );

  return (
    <div className="bg-sunken border border-slate-200 dark:border-gray-700 rounded-md overflow-hidden">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 [&>section+section]:border-l-0 [&>section]:border-slate-100 dark:[&>section]:border-gray-700">
        {/* COLUMN 1 — Trace Run Status */}
        <Section title="Trace Run" icon={<Icon.search className="w-3.5 h-3.5" />}>
          <Row>
            <Label width={LABEL_W_LEFT}>Trace Container</Label>
            <SingleSelect
              options={containerSelectOptions}
              value={traceUnit}
              onChange={(v) => {
                seededProRef.current = d.pronumber;
                setTraceUnit(v);
              }}
              enableSearch
              size="sm"
              usePortal
              showClearOption
              clearOptionLabel="—"
              placeholder="—"
              pinSelectedTop
              ariaLabel="Trace Container"
              className="w-44"
            />
            {traceRun?.(selectedTraceType, traceUnit)}
          </Row>
          {/* The left-column trace-run / event info is the latest EDI trace event
              — read-only for BOTH CSR and Dispatcher in legacy (nobody hand-edits
              it; it's fed by the tracing feed). Rendered read-only always, and no
              Change All checkboxes (you can't bulk-apply a field you can't edit). */}
          <Row>
            <Label width={LABEL_W_LEFT}>Trace Date / Carrier</Label>
            {rv('traceRunDatetime', 'Trace Date', { mono: true, width: 104 })}
            {rv('traceRunCarrier', 'Trace Carrier')}
          </Row>
          <Row>
            <Label width={LABEL_W_LEFT}>Event Date</Label>
            {rv('traceEventDatetime', 'Trace Event Date', { mono: true })}
          </Row>
          <Row>
            <Label width={LABEL_W_LEFT}>Trace Event</Label>
            {rv('traceEvent', 'Trace Event')}
          </Row>
          <Row>
            <Label width={LABEL_W_LEFT}>Trace Location</Label>
            {rv('traceLocation', 'Trace Location')}
          </Row>
          <Row>
            <Label width={LABEL_W_LEFT}>SCAC / Terminal</Label>
            <ReadValue value={d.scac} width={88} ariaLabel="SCAC" />
            <ReadValue value={d.rail} ariaLabel="Terminal" />
          </Row>
        </Section>

        {/* COLUMN 2 — Vessel / Rail / Customs / Holds */}
        <Section
          title="Vessel, Rail & Customs"
          icon={<Icon.ship className="w-3.5 h-3.5" />}
        >
          {isExport ? (
            <>
              {!hidden('traceMode') && (
              <Row>
                <Label width={LABEL_W_MID} bulkFieldName="traceMode" bulkEditable={!!modeEditable}>Mode</Label>
                {modeEditable ? (
                  <SelectInput
                    value={s(fv('traceMode'))}
                    options={MODE_OPTIONS}
                    width={88}
                    onChange={(v) => upd?.('traceMode', v)}
                    ariaLabel="Mode"
                  />
                ) : (
                  <ReadValue value={d.traceMode} width={88} ariaLabel="Mode" />
                )}
              </Row>
              )}
              {!hidden('tracePortOfOrigin') && (
              <Row>
                <Label width={LABEL_W_MID}>Trace POO / POD</Label>
                {rv('tracePortOfOrigin', 'Trace Port of Origin', { width: 104 })}
                {rv('tracePortOfDischarge', 'Trace Port of Discharge')}
              </Row>
              )}
              {!hidden('traceVessel') && (
              <Row>
                <Label width={LABEL_W_MID}>Trace Vessel / Voy</Label>
                {lcEditable && <BulkFieldCheckbox fieldName="traceVessel" />}
                {editText('traceVessel', 'Trace Vessel', { width: 104 })}
                {lcEditable && <BulkFieldCheckbox fieldName="traceVoyage" />}
                {editText('traceVoyage', 'Trace Voyage', { mono: true })}
              </Row>
              )}
              {!hidden('vesselSailDateEst') && (
              <Row>
                <Label width={LABEL_W_MID}>Sail Est / Act</Label>
                {lcEditable && <BulkFieldCheckbox fieldName="vesselSailDateEst" />}
                {editDateTime('vesselSailDateEst', 'Vessel Sail Est')}
                {lcEditable && <BulkFieldCheckbox fieldName="vesselSailDatetime" />}
                {editDateTime('vesselSailDatetime', 'Vessel Sail Act')}
              </Row>
              )}
              {!hidden('vesselArrivalDateEst') && (
              <Row>
                <Label width={LABEL_W_MID}>Arv Sch / Act</Label>
                {lcEditable && <BulkFieldCheckbox fieldName="vesselArrivalDateEst" />}
                {editDateTime('vesselArrivalDateEst', 'Vessel Arrival Est')}
                {lcEditable && <BulkFieldCheckbox fieldName="vesselArrivalDatetime" />}
                {editDateTime('vesselArrivalDatetime', 'Vessel Arrival Act')}
              </Row>
              )}
              {!hidden('arrivalGroundedDatetime') && (
              <Row>
                <Label width={LABEL_W_MID}>Grounded Date / Loc</Label>
                {lcEditable && <BulkFieldCheckbox fieldName="arrivalGroundedDatetime" />}
                {editDateTime('arrivalGroundedDatetime', 'Grounded Date')}
                {lcEditable && <BulkFieldCheckbox fieldName="arrivalGroundedLocation" />}
                {editText('arrivalGroundedLocation', 'Grounded Location')}
              </Row>
              )}
              {!hidden('bookingOnFileDate') && (
              <Row>
                <Label width={LABEL_W_MID}>Bkg on File / Equip Rel</Label>
                {lcEditable && <BulkFieldCheckbox fieldName="bookingOnFileDate" />}
                {editDate('bookingOnFileDate', 'Booking on File', { width: 104 })}
                {lcEditable && <BulkFieldCheckbox fieldName="equipReleaseDate" />}
                {editDate('equipReleaseDate', 'Equipment Release')}
              </Row>
              )}
              {!hidden('availableGoodToGoDatetime') && (
              <Row>
                <Label width={LABEL_W_MID} bulkFieldName="availableGoodToGoDatetime" bulkEditable={lcEditable}>Available</Label>
                {editDateTimeGated('availableGoodToGoDatetime', 'Available', lcEditable)}
              </Row>
              )}
              <Row>
                <Label width={LABEL_W_MID} bulkFieldName="preAdviseDatetime" bulkEditable={lcEditable}>Pre Advise</Label>
                {editDateTime('preAdviseDatetime', 'Pre Advise Date')}
              </Row>
              <Row>
                <Label width={LABEL_W_MID}>Haz Required</Label>
                {rv('hazReqDatetime', 'Haz Required Date', { mono: true })}
              </Row>
              <Row>
                <Label width={LABEL_W_MID} bulkFieldName="railBillStatus" bulkEditable={lcEditable}>Rail Bill Status</Label>
                {editSelect('railBillStatus', 'Rail Bill Status', RAILBILL_STATUS_OPTIONS)}
              </Row>
              {!hidden('traceTerminateDatetime') && (
              <Row>
                <Label width={LABEL_W_MID} bulkFieldName="traceTerminateDatetime" bulkEditable={tracingEditable}>Trace Terminate</Label>
                {editDateTimeGated('traceTerminateDatetime', 'Trace Terminate Date', tracingEditable, 'Trace Terminate')}
              </Row>
              )}
            </>
          ) : (
            <>
              {!hidden('traceMode') && (
              <Row>
                <Label width={LABEL_W_MID} bulkFieldName="traceMode" bulkEditable={!!modeEditable}>Mode</Label>
                {modeEditable ? (
                  <SelectInput
                    value={s(fv('traceMode'))}
                    options={MODE_OPTIONS}
                    width={88}
                    onChange={(v) => upd?.('traceMode', v)}
                    ariaLabel="Mode"
                  />
                ) : (
                  <ReadValue value={d.traceMode} width={88} ariaLabel="Mode" />
                )}
              </Row>
              )}
              {!hidden('etaDate') && (
              <Row>
                <Label width={LABEL_W_MID}>Original / Revised ETA</Label>
                {rv('etaDate', 'Original ETA', { mono: true, width: 104 })}
                {!hidden('revisedEtaDatetime') && (lcEditable ? (
                  <>
                    <BulkFieldCheckbox fieldName="revisedEtaDatetime" />
                    <div className="flex-1 min-w-0">
                      <DateTimePicker
                        mode="datetime"
                        inputVariant="compact"
                        selectionType="single"
                        use24Hour
                        usePortal
                        openDirection="down"
                        autoFlip
                        labelType="none"
                        placeholder="Revised ETA"
                        className="w-full [&_div]:!min-w-0"
                        value={parseMdYHm(s(fv('revisedEtaDatetime')))}
                        onChange={(v) => upd?.('revisedEtaDatetime', formatMdYHm(v as Date | null))}
                      />
                    </div>
                  </>
                ) : (
                  <ReadValue value={d.revisedEtaDatetime} mono ariaLabel="Revised ETA" />
                ))}
              </Row>
              )}
              {!hidden('tracePortOfOrigin') && (
              <Row>
                <Label width={LABEL_W_MID}>Trace POO / POD</Label>
                {rv('tracePortOfOrigin', 'Trace Port of Origin', { width: 104 })}
                {rv('tracePortOfDischarge', 'Trace Port of Discharge')}
              </Row>
              )}
              {!hidden('traceVessel') && (
              <Row>
                <Label width={LABEL_W_MID}>Trace Vessel / Voy</Label>
                {lcEditable && <BulkFieldCheckbox fieldName="traceVessel" />}
                {editText('traceVessel', 'Trace Vessel', { width: 104 })}
                {lcEditable && <BulkFieldCheckbox fieldName="traceVoyage" />}
                {editText('traceVoyage', 'Trace Voyage', { mono: true })}
              </Row>
              )}
              {!hidden('vesselSailDateEst') && (
              <Row>
                <Label width={LABEL_W_MID}>Sail Est / Act</Label>
                {lcEditable && <BulkFieldCheckbox fieldName="vesselSailDateEst" />}
                {editDateTime('vesselSailDateEst', 'Vessel Sail Est')}
                {lcEditable && <BulkFieldCheckbox fieldName="vesselSailDatetime" />}
                {editDateTime('vesselSailDatetime', 'Vessel Sail Act')}
              </Row>
              )}
              {!hidden('vesselArrivalDateEst') && (
              <Row>
                <Label width={LABEL_W_MID}>Arv Sch / Act</Label>
                {lcEditable && <BulkFieldCheckbox fieldName="vesselArrivalDateEst" />}
                {editDateTime('vesselArrivalDateEst', 'Vessel Arrival Est')}
                {lcEditable && <BulkFieldCheckbox fieldName="vesselArrivalDatetime" />}
                {editDateTime('vesselArrivalDatetime', 'Vessel Arrival Act')}
              </Row>
              )}

              <Divider />

              <Row>
                <Label width={LABEL_W_MID}>Loading Ramp</Label>
                <ReadValue value={d.loadingRamp} ariaLabel="Loading Ramp" />
              </Row>
              {!hidden('trainDepartDatetime') && (
              <Row>
                <Label width={LABEL_W_MID} bulkFieldName="trainDepartDatetime" bulkEditable={lcEditable}>Train Depart</Label>
                {editDateTime('trainDepartDatetime', 'Train Depart Date')}
              </Row>
              )}
              {!hidden('arrivalGroundedDatetime') && (
              <Row>
                <Label width={LABEL_W_MID}>Grounded Date / Loc</Label>
                {lcEditable && <BulkFieldCheckbox fieldName="arrivalGroundedDatetime" />}
                {editDateTime('arrivalGroundedDatetime', 'Grounded Date')}
                {lcEditable && <BulkFieldCheckbox fieldName="arrivalGroundedLocation" />}
                {editText('arrivalGroundedLocation', 'Grounded Location')}
              </Row>
              )}
              {!hidden('bookingOnFileDate') && (
              <Row>
                <Label width={LABEL_W_MID}>Bkg on File / Equip Rel</Label>
                {lcEditable && <BulkFieldCheckbox fieldName="bookingOnFileDate" />}
                {editDate('bookingOnFileDate', 'Booking on File', { width: 104 })}
                {lcEditable && <BulkFieldCheckbox fieldName="equipReleaseDate" />}
                {editDate('equipReleaseDate', 'Equipment Release')}
              </Row>
              )}

              <Divider />

              {!hidden('customsStatus') && (
              <Row>
                <Label width={LABEL_W_MID}>Customs / Rel Date</Label>
                {lcEditable ? (
                  <>
                    <BulkFieldCheckbox fieldName="customsStatus" />
                    {editSelect('customsStatus', 'Customs Status', CUSTOMS_STATUS_OPTIONS, { width: 104 })}
                  </>
                ) : (
                  rv('customsStatus', 'Customs Status', { width: 104 })
                )}
                {lcEditable && <BulkFieldCheckbox fieldName="customsRelDatetime" />}
                {editDateTime('customsRelDatetime', 'Customs Release Date')}
              </Row>
              )}
              {/* Line status / release + Other Holds are EDI-fed and locked for
                  both groups (legacy parity) — read-only, no Change All. */}
              {!hidden('lineStatus') && (
              <Row>
                <Label width={LABEL_W_MID}>Line / Rel Date</Label>
                {rv('lineStatus', 'Line Status', { width: 104 })}
                {rv('lineRelDatetime', 'Line Release Date', { mono: true })}
              </Row>
              )}
              {!hidden('otherHolds') && (
              <Row>
                <Label width={LABEL_W_MID}>Other Holds</Label>
                <ReadValue value={d.otherHolds} width={32} ariaLabel="Other Holds" />
              </Row>
              )}

              <Divider />

              {!hidden('availableGoodToGoDatetime') && (
              <Row>
                <Label width={LABEL_W_MID} bulkFieldName="availableGoodToGoDatetime" bulkEditable={lcEditable}>GWI Avail / GTG</Label>
                {editDateTimeGated('availableGoodToGoDatetime', 'GWI Available/GTG Date', lcEditable, 'Available/GTG')}
              </Row>
              )}
              {isThd && (
              <Row>
                <Label width={LABEL_W_MID} bulkFieldName="railLfdDate" bulkEditable={lcEditable}>THD LFD</Label>
                {editDate('railLfdDate', 'THD LFD')}
              </Row>
              )}
              {!hidden('outGateDatetime') && (
              <Row>
                <Label width={LABEL_W_MID} bulkFieldName="outGateDatetime" bulkEditable={tracingEditable}>Out Gate</Label>
                {editDateTimeGated('outGateDatetime', 'Out Gate Date', tracingEditable, 'Out Gate')}
              </Row>
              )}
              <Row>
                <Label width={LABEL_W_MID} bulkFieldName="preAdviseDatetime" bulkEditable={lcEditable}>Pre Advise</Label>
                {editDateTime('preAdviseDatetime', 'Pre Advise Date')}
              </Row>
              <Row>
                <Label width={LABEL_W_MID}>Haz Required</Label>
                {rv('hazReqDatetime', 'Haz Required Date', { mono: true })}
              </Row>
              <Row>
                <Label width={LABEL_W_MID} bulkFieldName="railBillStatus" bulkEditable={lcEditable}>Rail Bill Status</Label>
                {editSelect('railBillStatus', 'Rail Bill Status', RAILBILL_STATUS_OPTIONS)}
              </Row>
              {!hidden('traceTerminateDatetime') && (
              <Row>
                <Label width={LABEL_W_MID} bulkFieldName="traceTerminateDatetime" bulkEditable={tracingEditable}>Trace Terminate</Label>
                {editDateTimeGated('traceTerminateDatetime', 'Trace Terminate Date', tracingEditable, 'Trace Terminate')}
              </Row>
              )}
            </>
          )}
        </Section>

        {/* COLUMN 3 — Gnosis */}
        <Section title="Gnosis" icon={<Icon.info className="w-3.5 h-3.5" />}>
          {/* V1 shows portterminalname above Gnosis LFD for every move type
              (defs.pl @edit_layout_tracing col 3), not export-only. */}
          <Row>
            <Label width={LABEL_W_RIGHT}>Port Terminal</Label>
            {rv('terminal', 'Port Terminal')}
          </Row>
          {isExport && <Divider />}
          <Row>
            <Label width={LABEL_W_RIGHT}>Gnosis LFD</Label>
            <ReadValue value={d.yardPullDate} mono ariaLabel="Gnosis LFD" />
          </Row>
          {!hidden('perDiemSslInvRcvDate') && (
          <Row>
            <Label width={LABEL_W_RIGHT}>Gnosis Per Diem LFD</Label>
            <ReadValue value={d.perDiemSslInvRcvDate} mono ariaLabel="Gnosis Per Diem LFD" />
          </Row>
          )}
          {/* Scheduling / rail-pickup contacts live under the Gnosis info in the
              third column for every move type (legacy layout), not export-only. */}
          <Divider />
          <Row>
            <Label width={LABEL_W_RIGHT} bulkFieldName="schedulingContactType" bulkEditable={lcEditable}>Sched Contact Type</Label>
            {editSelect('schedulingContactType', 'Scheduling Contact Type', CONTACT_TYPE_OPTIONS)}
          </Row>
          <Row>
            <Label width={LABEL_W_RIGHT} bulkFieldName="schedulingContactInfo" bulkEditable={lcEditable}>Sched Contact Info</Label>
            {editTextArea('schedulingContactInfo', 'Scheduling Contact Info')}
          </Row>
          <Divider />
          <Row>
            <Label width={LABEL_W_RIGHT} bulkFieldName="railPickupContactType" bulkEditable={lcEditable}>Rail Pickup # Type</Label>
            {editSelect('railPickupContactType', 'Rail Pickup Contact Type', CONTACT_TYPE_OPTIONS)}
          </Row>
          <Row>
            <Label width={LABEL_W_RIGHT} bulkFieldName="railPickupContactInfo" bulkEditable={lcEditable}>Rail Pickup # Info</Label>
            {editTextArea('railPickupContactInfo', 'Rail Pickup Contact Info')}
          </Row>
        </Section>
      </div>
    </div>
  );
};

export default TracingView;
